import type { ColDef, RowClickedEvent } from 'ag-grid-community';
import { type FC, useEffect, useState } from 'react';

import { Button, Col, OverlayTrigger, Popover, Row } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { Clipboard, Plus } from 'react-bootstrap-icons';
import Select from 'react-dropdown-select';
import type { Option } from 'react-multi-select-component';
import EditblackIcon from '../../../assets/edit-icon.svg';
import VerticalDotMenuIcon from '../../../assets/vertical-dot-menu.svg';
import {
  useAuth,
  useMutationRequest,
  useNotifications,
  useQueryRequest,
} from '../../../modules/hooks';
import { endpoints } from '../../../modules/mappers/urls';
import {
  copyWhatsappTemplate,
  replaceTemplateVariablesWithIndices,
} from '../../../modules/utils/copyTemplates';
import {
  AutomaticMessagesManagerModal,
  type WhatsAppTemplate,
} from '../../Modals/MessageManagerModal';
import { DataTable } from '../DataTable';

interface PropertyConfigModalProps {
  show: boolean;
  propertyId: string;
  handleClose: () => void;
}

export const paymentStatusColorMapper: Record<string, string> = {
  CONFIRMED: 'primary',
  STARTED: 'warning',
};

export const PropertyAutomationsManager: FC<PropertyConfigModalProps> = ({
  show,
  propertyId,
  handleClose,
}) => {
  const { credentialsInfo } = useAuth()!;
  const { setSimpleToasts } = useNotifications()!;

  const [propertyTemplates, setPropertyTemplates] = useState<WhatsAppTemplate[]>([]);
  const [templateOptions, setTemplateOptions] = useState<Option[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);

  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | undefined>(
    undefined,
  );
  const [optionSelectedTemplate, setOptionSelectedTemplate] = useState<Option[]>([]);

  const { data: messageTemplates, loading: loadingTemplates } = useQueryRequest<
    WhatsAppTemplate[]
  >(endpoints.MESSAGING_TEMPLATES, {
    authToken: credentialsInfo?.token,
  });

  const getPropertyUrl = `${endpoints.PROPERTIES}/${propertyId}/messaging-templates`;
  const {
    data: propertyTemplatesResult,
    loading: loadingPropertyTemplates,
    refetch: getPropertyTemplates,
  } = useQueryRequest<WhatsAppTemplate[]>(getPropertyUrl, {
    authToken: credentialsInfo?.token,
  });

  const propertyTemplateUrl = `${endpoints.PROPERTIES}/${propertyId}/messaging-templates/${optionSelectedTemplate?.[0]?.value}`;

  const [
    { data: addTemplateData, loading: loadingAddTemplateData },
    addMessagingTemplate,
  ] = useMutationRequest(propertyTemplateUrl, 'patch', {
    authToken: credentialsInfo?.token,
  });

  const onClose = () => {
    setTemplateOptions([]);
    setPropertyTemplates([]);
    handleClose();
  };

  const handleAddTemplate = () => {
    const messageTemp = propertyTemplates.find(
      (item) => item.templateId === optionSelectedTemplate?.[0].value,
    );

    const data = {
      subject: optionSelectedTemplate[0].label,
      message: messageTemp?.message,
    };
    addMessagingTemplate(data);
  };

  const handleAddPropertyModalTemplate = () => {
    getPropertyTemplates();
  };

  const onShowEditModal = (item: WhatsAppTemplate) => {
    setSelectedTemplate(item);
    setShowEditModal(true);
  };

  const renderMenuIcon = (item: RowClickedEvent<WhatsAppTemplate>) => {
    return (
      <div className="menu-icon-container-style">
        <OverlayTrigger
          trigger="click"
          placement="left"
          rootClose
          overlay={
            <Popover id="popover-basic">
              <Popover.Body>
                <div
                  onClick={(e) => {
                    document.body.click();
                    onShowEditModal(item.data || ({} as WhatsAppTemplate));
                  }}
                  style={{ paddingTop: '8px', paddingBottom: '8px' }}
                  className="edit-damage-report-container"
                >
                  <img
                    className="edit-icon-image-style"
                    src={EditblackIcon}
                    alt="edit-black-icon"
                  />
                  <div className="edit-text-style">Edit</div>
                </div>
                <div
                  onClick={(e) => {
                    document.body.click();

                    replaceTemplateVariablesWithIndices(
                      setSimpleToasts,
                      item.data?.message,
                    );
                  }}
                  style={{ paddingTop: '8px', paddingBottom: '8px' }}
                  className="edit-damage-report-container"
                >
                  <Clipboard />
                  <div className="edit-text-style">Copy message template</div>
                </div>
                <div
                  onClick={(e) => {
                    document.body.click();
                    copyWhatsappTemplate(
                      setSimpleToasts,
                      item.data?.whatsappTemplateId ||
                        item.data?.template?.whatsappTemplateId,
                    );
                  }}
                  style={{ paddingTop: '8px', paddingBottom: '8px' }}
                  className="edit-damage-report-container"
                >
                  <Clipboard />
                  <div className="edit-text-style">Copy WhatsApp template ID</div>
                </div>
              </Popover.Body>
            </Popover>
          }
        >
          <div>
            <img src={VerticalDotMenuIcon} alt="vertical-icon" />
          </div>
        </OverlayTrigger>
      </div>
    );
  };

  useEffect(() => {
    if (messageTemplates && messageTemplates?.result && show) {
      const amenitiesOptions = messageTemplates.result.map((template) => ({
        value: template.templateId,
        label: template.subject,
      }));
      setTemplateOptions(amenitiesOptions);
    }
  }, [messageTemplates, show]);

  useEffect(() => {
    if (propertyTemplatesResult && propertyTemplatesResult?.result && show) {
      setPropertyTemplates(propertyTemplatesResult?.result);
    }
  }, [propertyTemplatesResult, show]);

  useEffect(() => {
    if (addTemplateData && !loadingAddTemplateData && show) {
      getPropertyTemplates();
    }
  }, [addTemplateData, show]);

  const [columns] = useState<ColDef<WhatsAppTemplate>[]>([
    {
      valueGetter: (params) => {
        const { data: template } = params;
        const name = template
          ? `${template?.templateId || template?.template?.templateId}`
          : '-';
        return name;
      },
      headerName: 'Template',

      flex: 4,
    },
    {
      valueGetter: (params) => {
        const { data: template } = params;
        const name = template ? `${template?.template?.whatsappTemplateId || '-'}` : '-';
        return name;
      },
      headerName: 'Whatsapp Template',

      flex: 4,
    },
    { field: 'subject', headerName: 'Template Name', flex: 4 },
    { field: 'message', headerName: 'Message', flex: 4 },

    {
      headerName: 'ACTIONS',
      cellRenderer: renderMenuIcon,
      flex: 1,
    },
  ]);

  return (
    <Modal
      show={show}
      onHide={() => {
        handleClose();
      }}
      size="xl"
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title className="h5">Templates</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col sm={10}>
            <Select
              options={templateOptions}
              values={optionSelectedTemplate}
              onChange={(values) => {
                setOptionSelectedTemplate(values);
              }}
              loading={loadingTemplates}
            />
          </Col>
          <Col
            sm={2}
            style={{ display: 'flex', justifyContent: 'right' }}
            onClick={handleAddTemplate}
            disable={loadingAddTemplateData}
          >
            <Button>
              {loadingAddTemplateData ? (
                'Loading ...'
              ) : (
                <>
                  <Plus /> Add Template
                </>
              )}
            </Button>
          </Col>
        </Row>
        <div className="mt-24px">
          <DataTable
            showSearchInput={false}
            changeState={0}
            rowData={propertyTemplates}
            columnDefs={columns}
            pagination
            paginationPageSize={5}
            loading={loadingPropertyTemplates}
          />
        </div>
      </Modal.Body>
      <Modal.Footer style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div>
          <Button className="cancel-button" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Modal.Footer>

      {selectedTemplate && (
        <AutomaticMessagesManagerModal
          show={showEditModal}
          defaultTemplate={selectedTemplate}
          handleClose={() => setShowEditModal(false)}
          isEdit
          propertiesAddFunction={handleAddPropertyModalTemplate}
          propertyId={propertyId}
        />
      )}
    </Modal>
  );
};
